import React, { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Badge, Button, TextInput } from "@mantine/core";
import { format, parseISO } from "date-fns";
import { useDownloadExport } from "../../../../hooks/QueryHooks";
import { ExportResponse } from "../../../../services/exports/exportsEndpoints.interface";
import notify from "../../../../utils/notify";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { AxiosResponse } from "axios";

interface ExportsListProps {
  exports: ExportResponse[];
}

const ExportsList: React.FC<ExportsListProps> = ({ exports }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const downloadExportMutation = useDownloadExport();
  const workspace = useWorkspaceStore((state) => state.workspace);

  const formatDate = (dateString: string): string => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy h:mm a");
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadgeColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case "completed":
        return "green";
      case "processing":
        return "blue";
      case "failed":
        return "red";
      case "pending":
        return "yellow";
      default:
        return "gray";
    }
  };

  // Try to use the export's direct download_url if available in the export data
  const handleDownload = (exportId: string, directDownloadUrl?: string) => {
    if (!workspace?.id) return;
    
    // If we have a direct download URL from the export data, use it directly
    if (directDownloadUrl) {
      console.log("Using direct download URL:", directDownloadUrl);
      window.open(directDownloadUrl, "_blank");
      
      notify({
        messageList: ["Download started in new tab"],
        type: "success",
      });
      return;
    }
    
    // Show loading notification only for API method
    notify({
      messageList: ["Preparing download..."],
      type: "info",
    });
    
    // Otherwise use the API
    console.log(`Initiating download for export ID: ${exportId}`);
    
    downloadExportMutation.mutate(
      { workspace_id: workspace.id, id: exportId },
      {
        onSuccess: (response: AxiosResponse<string>) => {
          console.log("Download response:", response);
          
          // The response should contain the download URL in data property
          const downloadUrl = response.data;
          console.log("Download URL:", downloadUrl);
          
          if (downloadUrl) {
            // Open in new tab - most reliable cross-browser approach
            const newWindow = window.open("", "_blank");
            
            if (newWindow) {
              newWindow.location.href = downloadUrl;
              
              notify({
                messageList: ["Download started in new tab"],
                type: "success",
              });
            } else {
              // If popup is blocked, show a message with the URL
              console.log("Popup blocked. Direct URL:", downloadUrl);
              
              notify({
                messageList: ["Popup blocked. Please allow popups and try again."],
                type: "warning",
              });
            }
          } else {
            notify({
              messageList: ["No download URL received from server"],
              type: "failure",
            });
          }
        },
        onError: (error: any) => {
          notify({
            messageList: [
              error?.response?.data?.message || "An error occurred while downloading the export",
            ],
            type: "failure",
          });
        },
      }
    );
  };

  const filteredExports = exports.filter((exportItem) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      exportItem.view?.view_name?.toLowerCase().includes(searchLower) ||
      exportItem.status.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="flex flex-col w-full min-h-full">
      {/* Search Bar */}
      <div className="mb-4">
        <TextInput
          placeholder="Search exports..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftSection={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
        />
      </div>

      {/* Exports List */}
      <div className="space-y-3 min-h-full">
        {filteredExports.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchQuery ? "No exports match your search" : "No exports found"}
          </div>
        ) : (
          filteredExports.map((exportItem) => (
            <div
              key={exportItem.id}
              className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-2xl hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center space-x-4">
                <Button
                  variant="subtle"
                  size="sm"
                  className="text-gray-600 hover:bg-gray-100"
                  disabled={exportItem.status.toLowerCase() !== "completed"}
                  onClick={() => handleDownload(exportItem.id, exportItem.download_url)}
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                </Button>
                <div>
                  <div className="font-medium text-gray-900">
                    {exportItem.view?.view_name || "Unnamed Export"}
                  </div>
                  <div className="text-sm text-gray-500">
                    {exportItem.view?.view_description || "No description"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Created: {formatDate(exportItem.created_at)}
                    {exportItem.completed_at && (
                      <span className="ml-3">
                        Completed: {formatDate(exportItem.completed_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Badge color={getStatusBadgeColor(exportItem.status)} size="md">
                {exportItem.status}
              </Badge>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExportsList;
